import { useEffect, useRef, useState } from 'react';

import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

interface CountUpProps {
  value: number;
  /** How long the run takes. Long enough to be seen, short enough not to wait. */
  durationMs?: number;
  className?: string;
  /** Printed after the number, inside the same run — e.g. `%`. */
  suffix?: string;
}

/**
 * A headline number that counts up to itself.
 *
 * Only worth it on a figure somebody is *reading as a figure* — the tiles on
 * the platform overview, where the movement is what draws the eye to the number
 * that changed since yesterday. Not for a value in a table.
 *
 * Two things it is careful about:
 *
 * - **Reduced motion.** Anyone who has asked their machine for less movement
 *   gets the final number immediately, not a slower run.
 * - **Screen readers.** The digits ticking over are hidden and the real value is
 *   announced once, or the number would be read out sixty times on the way up.
 */
export function CountUp({ value, durationMs = 900, className, suffix }: CountUpProps) {
  const [shown, setShown] = useState(value);
  /** What is on screen right now, so an interrupted run resumes from it. */
  const shownRef = useRef(value);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const from = shownRef.current;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || from === value) {
      shownRef.current = value;
      setShown(value);
      return;
    }

    const startedAt = performance.now();

    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / durationMs);
      // Ease-out: fast off the mark, settling onto the real figure rather than
      // stopping dead on it.
      const eased = 1 - (1 - progress) ** 3;
      const next = Math.round(from + (value - from) * eased);

      shownRef.current = next;
      setShown(next);

      if (progress < 1) frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [value, durationMs]);

  return (
    <>
      <span className={cn('tabular-nums', className)} aria-hidden>
        {formatNumber(shown)}
        {suffix}
      </span>
      <span className="sr-only">
        {formatNumber(value)}
        {suffix}
      </span>
    </>
  );
}
